import { computed, ref } from 'vue';
import { SERVICE_PROCESS_NAVIGATION, SERVICE_EDITOR_ROUTES } from '@/config/studioNavigation';
import { STUDIO_DISPLAY_SECTIONS } from '@/config/studioDisplay';
import { defineStore } from 'pinia';
import { delCookie, getCookie } from '@/utils/cookie';

export const authorizationModules = [
  { code: 'image_editing', name: '图片编辑' },
  { code: 'customer_management', name: '客资管理系统' },
  { code: 'sales', name: '销售系统' }
] as const;
export type ModuleCode = typeof authorizationModules[number]['code'];
export interface CurrentAuthorization {
  studioId: number;
  enabled: boolean;
  accountLimit: number;
  usedAccountCount: number;
  userId: number;
  mainAccount: boolean;
  modules: { code: ModuleCode; enabled: boolean; expiresAt: string | null }[];
}

export function moduleForRoute(name: unknown): ModuleCode | null {
  const routeName = typeof name === 'string' ? name : '';
  if (routeName === 'Design' || routeName.startsWith('OrderDesign') || routeName.startsWith('DesignTemplate') || routeName === 'Proofing' || routeName === 'DesignProofing'
    || routeName === 'DesignProduction' || routeName === 'DesignOrders' || Object.keys(SERVICE_EDITOR_ROUTES).includes(routeName)) return 'image_editing';
  if (SERVICE_PROCESS_NAVIGATION.some(item => item.name === routeName)) return 'customer_management';
  // 排期目前沿用后端 customer_management 授权，本轮只恢复入口，不新增收费模块。
  if (['Service', 'Order', 'OrderDetail', 'OrderRecycle', 'StudioMember', 'CustomerLeads', 'CustomerWorkbench', 'CustomerInsights',
    'StudioWorkbench', 'StudioOrderCenter', 'StudioBusinessSetup', 'Scheduling', 'CustomerJourney', 'ShootingProcess', 'MakeupProcess', 'ColorMixingProcess', 'FilmSelectionProcess',
    'StudioProcess', 'ShootingType', 'StudioDisplayShootingType', 'ShootingLevel', 'ScenicSpot',
    'Package', 'StudioDisplayPackages', 'StudioDisplayLocations', 'PackageCategory', 'MvCategory', 'ConsumptionType', 'DocumentCode',
    'PaymentRecords', 'CustomerConsumptionRecords', 'RefundAndReturnRecords'].includes(routeName)) return 'customer_management';
  if (['DailyReport', 'MonthlyReport', 'AnnualReport', 'AnnualSurplusAnalysis',
    'MonthlyProcessReport', 'MonthlyUnpaidReport', 'OddIncomeManagement'].includes(routeName)) return 'customer_management';
  if (routeName === 'StudioDisplayOverview' || STUDIO_DISPLAY_SECTIONS.some(section => section.name === routeName || section.relatedRoutes?.includes(routeName))
    || ['ProductShowcase', 'Product', 'ProductCategory'].includes(routeName)) return 'sales';
  return null;
}

function currentSession(): string {
  const token = getCookie('photo_user_token');
  return token ? `${getCookie('photo_header_prefix')}\n${token}` : '';
}

function parseAuthorization(data: unknown): CurrentAuthorization {
  const value = data as CurrentAuthorization | null;
  const validId = (id: unknown) => Number.isSafeInteger(id) && Number(id) > 0;
  const validCount = (count: unknown) => Number.isSafeInteger(count) && Number(count) >= 0;
  if (!value || !validId(value.studioId) || !validId(value.userId) || typeof value.enabled !== 'boolean'
    || !validCount(value.accountLimit) || !validCount(value.usedAccountCount) || typeof value.mainAccount !== 'boolean'
    || !Array.isArray(value.modules) || value.modules.length !== authorizationModules.length
    || new Set(value.modules.map(module => module?.code)).size !== authorizationModules.length
    || value.modules.some(module => !module || !authorizationModules.some(definition => definition.code === module.code)
      || typeof module.enabled !== 'boolean' || !(module.expiresAt === null || (typeof module.expiresAt === 'string'
        && /(?:Z|[+-]\d{2}:\d{2})$/i.test(module.expiresAt) && Number.isFinite(Date.parse(module.expiresAt)))))) {
    throw new Error('无法确认当前影楼授权，请重新检查或联系平台管理员。');
  }
  if (!value.enabled) throw new Error('当前影楼授权已停用，请联系平台管理员。');
  return value;
}

export const useAuthorizationStore = defineStore('studio-authorization', () => {
  const current = ref<CurrentAuthorization | null>(null);
  const loading = ref(false);
  const error = ref('');
  const session = ref('');
  let generation = 0;
  let pending: { session: string; promise: Promise<CurrentAuthorization> } | null = null;

  const available = computed(() => Boolean(current.value?.enabled && session.value && session.value === currentSession()));

  function reset() {
    generation++;
    current.value = null;
    session.value = '';
    error.value = '';
    loading.value = false;
    pending = null;
  }

  function hasModule(code: ModuleCode): boolean {
    if (!available.value) return false;
    const module = current.value?.modules.find(item => item.code === code);
    return Boolean(module?.enabled && (module.expiresAt === null || Date.parse(module.expiresAt) > Date.now()));
  }

  function canAccessRoute(name: unknown): boolean {
    if (!available.value) return false;
    const module = moduleForRoute(name);
    return module === null || hasModule(module);
  }

  async function refresh(): Promise<CurrentAuthorization> {
    const requestedSession = currentSession();
    if (!requestedSession) { reset(); throw new Error('请先登录。'); }
    if (pending?.session === requestedSession) return pending.promise;
    const request = ++generation;
    // Keep the current session's menus visible while the route guard awaits a fresh
    // grant. A changed login must never reuse it; rejected checks still clear it below.
    if (session.value !== requestedSession) current.value = null;
    session.value = requestedSession;
    loading.value = true;
    error.value = '';

    const promise = (async () => {
      try {
        // Defer the HTTP import until Pinia is installed; the shared client owns a loading store.
        const { default: http } = await import('@/utils/http');
        if (request !== generation || currentSession() !== requestedSession) throw new Error('登录状态已变更，请重新检查授权。');
        const response = await http({ url: '/api/photo-studio/authorization/current', method: 'GET', silentError: true, skipAuthRedirect: true });
        if (request !== generation || currentSession() !== requestedSession) throw new Error('登录状态已变更，请重新检查授权。');
        const result = parseAuthorization(response.data);
        current.value = result;
        return result;
      } catch (cause) {
        if (request === generation && currentSession() === requestedSession) {
          current.value = null;
          error.value = cause instanceof Error ? cause.message : '无法读取影楼授权，请稍后重试。';
          const code = String((cause as { code?: unknown })?.code || '');
          if (code === '401' || code === '403') {
            delCookie('photo_user_token');
            delCookie('photo_header_prefix');
            session.value = '';
          }
        }
        throw cause;
      } finally {
        if (request === generation) { loading.value = false; pending = null; }
      }
    })();
    pending = { session: requestedSession, promise };
    return promise;
  }

  return { current, loading, error, available, reset, refresh, hasModule, canAccessRoute };
});
